import { Node, Edge } from 'reactflow';

// Types for converting old array format surveys to React Flow format
export type SurveyQuestionType = 'rating' | 'radio' | 'checkbox' | 'textfield' | 'done';

export interface SurveyConditionalConfig {
  condition: string;
  number: number;
  response_question: string;
  type: SurveyQuestionType;
}

export interface SurveyQuestionTemplate {
  number: number;
  type: SurveyQuestionType;
  question?: string;
  type_config?: {
    format?: string;
    rate_amount?: number;
    radio_options?: string[];
    checkbox_options?: string[];
  };
  conditional?: boolean;
  conditional_config?: SurveyConditionalConfig;
}

export interface Survey {
  id: number;
  typeform_id?: string | null;
  name: string;
  description?: string;
  icon_url?: string | null;
  questions: {
    nodes: Node[];
    edges: Edge[];
  } | null;
  is_active?: boolean | number | null;
  data_reward?: string | null;
  brand_id?: string | number | null;
  link?: string | null;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
}

// Node data interface for QuestionNode callbacks
export interface QuestionNodeData {
  label?: string;
  questionType?: 'options' | 'choices';
  createConditionalQuestions?: boolean;
  isMultiSelection?: boolean; // True when multiple nodes are selected
  onUpdate?: (nodeId: string, newLabel: string) => void;
  onAddOption?: (nodeId: string, option: string, type?: 'options' | 'choices', createConditional?: boolean) => void;
  onToggleConditionalMode?: (nodeId: string, enabled: boolean) => void;
  onRemoveOption?: (nodeId: string, option: string) => void;
  onUpdateRatingConfig?: (nodeId: string, format: 'numerical' | 'satisfaction', rateAmount: number) => void;
  onAddRatingScale?: (nodeId: string) => void;
  onToggleOptional?: (nodeId: string, isOptional: boolean) => void;
  onUnlinkNode?: (nodeId: string) => void;
  onReconnectConditionalEdge?: (parentNodeId: string, option: string) => void;
  onReconnectAllOrphanedSubQuestions?: (parentNodeId: string) => void;
  meta?: SurveyQuestionTemplate;
  question?: string;
  parentConditionalSourceId?: string;
  parentConditionalOption?: string;
}

// Re-export React Flow types for convenience
export type { Node, Edge, Connection, NodeProps, EdgeProps } from 'reactflow';

// ============================================================================
// NEW CARD FORMAT TYPES (Solution 3: Card + Wizard)
// ============================================================================

/** Stable ID for the synthetic "done" question appended as the last step (reward/completion marker). */
export const DONE_QUESTION_ID = 'survey-end';

/** Stable ID for the done step in emitted JSON (survey reader expects this). */
export const DONE_STEP_JSON_ID = 'done';

/** Minimal "done" step as stored in JSON (reward/completion marker). id + number + type for reader compatibility. */
export interface DoneStepMinimal {
  id: typeof DONE_STEP_JSON_ID;
  number: number;
  type: 'done';
}

/**
 * Card-based survey question format
 * Replaces the complex node/edge format with a simpler, more intuitive structure
 */
export interface CardQuestion {
  id: string;                          // UUID
  number: number | string;             // 1, 2, "2.1", "2.2"
  text: string;                        // Question text
  type: SurveyQuestionType;
  required: boolean;
  config: {
    // Rating
    format?: 'numerical' | 'satisfaction';
    rateAmount?: number;
    // Radio/Checkbox
    options?: string[];
  };
  pathId?: string;                     // If part of a conditional path
}

/**
 * Survey path represents conditional logic
 * A path is triggered by specific answers and shows a sequence of questions
 */
export interface SurveyPath {
  id: string;                          // UUID
  name: string;                        // User-friendly name, e.g., "Designer Path"
  trigger: {
    questionId: string;                // Which question triggers this path
    values: string[];                  // Which answer(s) trigger it (must match the option that shows this branch)
    label?: string;                    // Optional human-readable label (mirrors builder edge; helps reader validation)
  };
  questionIds: string[];               // Questions in this path (ordered)
  afterPath: 'continue' | 'end';      // What happens after path completes
}

/**
 * Card-based survey format
 * This is the new storage format that replaces nodes/edges
 */
export interface CardSurvey {
  format: 'card-v1';
  questions: CardQuestion[];
  paths: SurveyPath[];
}

/**
 * Helper type to check if survey is in card format
 */
export function isCardSurvey(questions: unknown): questions is CardSurvey {
  return (
    questions != null &&
    typeof questions === 'object' &&
    'format' in (questions as object) &&
    (questions as { format?: string }).format === 'card-v1'
  );
}

/**
 * Helper type to check if survey is in old flow format
 */
export function isFlowSurvey(questions: unknown): questions is { nodes: Node[]; edges: Edge[] } {
  const o = questions as Record<string, unknown> | null | undefined;
  return (
    o != null &&
    typeof o === 'object' &&
    'nodes' in o &&
    'edges' in o &&
    Array.isArray(o.nodes) &&
    Array.isArray(o.edges)
  );
}

